(() => {
  const STORAGE_KEY = 'zentrobadWishlist';
  const PAGE_PATH = '/pages/wunschliste';
  const nameCache = new Map();
  let renderVersion = 0;
  let migrationPending = null;
  const currentNames = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!nameCache.has(handle)) {
      nameCache.set(handle, fetch(`/products/${encodeURIComponent(handle)}?view=zentrobad-title`)
        .then(response => response.ok ? response.json() : response.status === 404 ? { missing: true } : null).catch(() => null));
    }
    return nameCache.get(handle);
  };

  const read = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.id) : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, 100)));
    window.dispatchEvent(new CustomEvent('zentrobad:wishlist-change', { detail: { items } }));
  };

  const escapeHtml = (value) =>
    String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const normalizeUrl = (item) => {
    try {
      const url = new URL(item.url || `/products/${item.handle}`, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return '#';
      url.searchParams.delete('variant');
      if (item.variantId && !item.needsReselect) url.searchParams.set('variant', item.variantId);
      return url.pathname + url.search + url.hash;
    } catch { return '#'; }
  };

  const normalizeOption = value => String(value || '').toLocaleLowerCase('de-DE')
    .replace(/hochgl(?:anz|änzend)|glänzend lackiert/g, 'glänzend')
    .replace(/\blackiert\b/g, '').replace(/\s+/g, ' ').trim();

  const migrateItem = async item => {
    const handle = item.handle || item.url?.match(/\/products\/([^/?#]+)/)?.[1];
    const names = await currentNames(handle);
    if (names?.missing) return { ...item, unavailable: true };
    if (!names?.variants?.length) return item;
    let redirects = names.variantRedirects || {};
    if (typeof redirects === 'string') { try { redirects = JSON.parse(redirects); } catch { redirects = {}; } }
    let savedId = item.variantId;
    if (!savedId && item.url) {
      try { savedId = new URL(item.url, window.location.origin).searchParams.get('variant'); } catch {}
    }
    const oldId = String(savedId || '').split('/').pop();
    const redirect = redirects[oldId];
    const currentId = String(typeof redirect === 'object' ? redirect.variantId : redirect || oldId);
    let variant = names.variants.find(v => String(v.id) === currentId);
    if (!variant && item.variantTitle && item.variantTitle !== 'Default Title') {
      const savedOptions = item.variantTitle.split(/\s*\/\s*/).map(normalizeOption);
      const matches = names.variants.filter(v => v.options.length === savedOptions.length &&
        v.options.every((value, index) => normalizeOption(value) === savedOptions[index]));
      if (matches.length === 1) variant = matches[0];
    }
    const next = { ...item, id: names.handle, handle: names.handle, url: names.url, unavailable: false };
    if (!variant) return { ...next, variantId: null, title: names.title, needsReselect: true };
    return { ...next, variantId: variant.id, variantTitle: variant.title,
      title: names.variantTitles?.[String(variant.id)] || names.title,
      image: variant.image || item.image, needsReselect: false };
  };

  const migrateSavedItems = async () => {
    const previous = read();
    const migrated = await Promise.all(previous.map(migrateItem));
    // Preserve additions/removals made while product data was loading.
    const current = read();
    const updates = new Map(previous.map((item, index) => [JSON.stringify(item), migrated[index]]));
    const seen = new Set();
    const next = current.map(item => updates.get(JSON.stringify(item)) || item).filter(item => {
      if (seen.has(String(item.id))) return false;
      seen.add(String(item.id)); return true;
    });
    if (JSON.stringify(next) !== JSON.stringify(current)) write(next);
  };

  const updateCounts = (items = read()) => {
    document.querySelectorAll('[data-zentrobad-wishlist-count]').forEach((element) => {
      element.hidden = items.length === 0;
      element.textContent = items.length ? String(items.length) : '';
      element.setAttribute('aria-label', `${items.length} gemerkte Produkte`);
    });
  };

  const renderPage = () => {
    if (window.location.pathname.replace(/\/$/, '') !== PAGE_PATH) return;

    const main = document.querySelector('#MainContent');
    if (!main) return;

    const items = read();
    const version = ++renderVersion;
    document.title = 'Wunschliste – Zentrobad';
    const cards = items
      .map((item) => {
        const url = normalizeUrl(item);
        return `
          <article class="zentrobad-wishlist-card" data-zentrobad-wishlist-item="${escapeHtml(item.id)}">
            <a class="zentrobad-wishlist-card__media" href="${escapeHtml(url)}">
              ${
                item.image
                  ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">`
                  : '<span aria-hidden="true"></span>'
              }
            </a>
            <div class="zentrobad-wishlist-card__body">
              <a class="zentrobad-wishlist-card__title" href="${escapeHtml(url)}">${escapeHtml(item.title)}</a>
              ${item.variantTitle ? `<p>${escapeHtml(item.variantTitle).replaceAll(' / ', ', ')}</p>` : ''}
              ${item.unavailable ? '<p>Dieses Produkt ist derzeit nicht verfügbar.</p>' : item.needsReselect ? '<p>Die gespeicherte Ausführung wurde geändert. Bitte wähle deine Variante erneut aus.</p>' : ''}
              <div class="zentrobad-wishlist-card__actions">
                ${item.unavailable ? '<a class="zentrobad-wishlist-card__button" href="/collections/all">Produkte entdecken</a>' : `<a class="zentrobad-wishlist-card__button" href="${escapeHtml(url)}">${item.needsReselect ? 'Variante neu auswählen' : 'Zum Produkt'}</a>`}
                <button type="button" data-zentrobad-wishlist-remove="${escapeHtml(item.id)}">Entfernen</button>
              </div>
            </div>
          </article>
        `;
      })
      .join('');

    main.innerHTML = `
      <section class="zentrobad-wishlist-page">
        <div class="zentrobad-wishlist-page__header">
          <p>Merkliste</p>
          <h1>Wunschliste</h1>
          <span>${items.length ? `${items.length} gemerkte Produkte` : 'Noch keine Produkte gemerkt'}</span>
        </div>
        ${
          items.length
            ? `<div class="zentrobad-wishlist-page__grid">${cards}</div>`
            : `<div class="zentrobad-wishlist-page__empty">
                <p>Merke Produkte über das Herz auf der Produktseite. Sie bleiben in diesem Browser gespeichert.</p>
                <a href="/collections/all">Produkte entdecken</a>
              </div>`
        }
      </section>
    `;

    main.querySelectorAll('[data-zentrobad-wishlist-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        const nextItems = read().filter((item) => String(item.id) !== String(button.dataset.zentrobadWishlistRemove));
        write(nextItems);
        renderPage();
      });
    });
    items.forEach(async (item) => {
      const handle = item.handle || item.url?.match(/\/products\/([^/?#]+)/)?.[1];
      const names = await currentNames(handle);
      if (!names || renderVersion !== version) return;
      const variantId = item.variantId || new URL(normalizeUrl(item), window.location.origin).searchParams.get('variant');
      const title = names.variantTitles?.[String(variantId || '').split('/').pop()] || names.title;
      if (!title) return;
      const card = [...main.querySelectorAll('[data-zentrobad-wishlist-item]')]
        .find(element => element.dataset.zentrobadWishlistItem === String(item.id));
      if (!card) return;
      card.querySelector('.zentrobad-wishlist-card__title').textContent = title;
      const img = card.querySelector('img');
      if (img) img.alt = title;
    });
  };

  const init = () => {
    updateCounts();
    renderPage();
    if (window.location.pathname.replace(/\/$/, '') === PAGE_PATH && !migrationPending) {
      migrationPending = migrateSavedItems().catch(() => {}).finally(() => { migrationPending = null; });
    }
  };

  window.addEventListener('zentrobad:wishlist-change', (event) => {
    updateCounts(event.detail?.items || read());
    renderPage();
  });
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) init();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
